import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItemButton, ListItemText } from '@mui/material';

import theme from 'theme';

import { scrollToSection } from './helpers';
import { SECTIONS } from './types';
import { NAV_OFFSET } from './constants';
import { global } from './styles';

const styles = {
  root: {
    left: 0,
    display: 'inline-flex',
  },
  absolute: {
    position: 'absolute',
    top: `calc(100vh + ${NAV_OFFSET}px)`,
  },
  sticky: {
    position: 'sticky',
    top: NAV_OFFSET,
  },
  button: {
    paddingLeft: theme.spacing(6),
    borderTopRightRadius: 24,
    borderBottomRightRadius: 24,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, .1)',
    },
    '& .MuiListItemText-primary': {
      ...global.paragraph1,
      color: 'white',
      fontWeight: 600,
    },
  },
} as const;

const AdvocacyNav: FC = () => {
  const { t } = useTranslation();
  const [sticky, setSticky] = useState(false);

  const boxStyle = {
    ...styles.root,
    ...(sticky ? styles.sticky : styles.absolute),
  };

  useEffect(() => {
    const element = document.getElementById('advocacyNav');
    const initialStickyPoint = element.offsetTop - NAV_OFFSET;

    function scrollHandler() {
      const { scrollY } = window;
      setSticky(scrollY >= initialStickyPoint);
      setActive();
    }

    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  function setActive() {
    const matcher = {
      introduction: isDocked(SECTIONS.introduction),
      organicInsights: isDocked(SECTIONS.organicInsights),
      shares: isDocked(SECTIONS.shares),
      advocates: isDocked(SECTIONS.advocates),
      executives: isDocked(SECTIONS.executives),
      takeawaysAndNextSteps: isDocked(SECTIONS.takeawaysAndNextSteps),
    };

    Object.keys(matcher).forEach(key => {
      const element = document.getElementById(`${key}-button`);
      element.style.backgroundColor = '';
    });

    const trueAttributes = Object.keys(matcher).filter(key => !!matcher[key]);
    const activeElement = document.getElementById(`${trueAttributes[0]}-button`);

    if (activeElement) {
      activeElement.style.backgroundColor = 'rgba(255, 255, 255, .1)';
    }
  }

  function isDocked(divId: string) {
    const element = document.getElementById(divId);
    const rect = element.getBoundingClientRect();

    return rect.top === 0;
  }

  return (
    <Box id="advocacyNav" style={boxStyle}>
      <List>
        <ListItemButton
          id="introduction-button"
          sx={styles.button}
          onClick={() => scrollToSection('introduction')}
        >
          <ListItemText primary={t('advocacy:introduction')} />
        </ListItemButton>
        <ListItemButton
          id="organicInsights-button"
          sx={styles.button}
          onClick={() => scrollToSection('organicInsights')}
        >
          <ListItemText primary={t('advocacy:organicInsights')} />
        </ListItemButton>
        <ListItemButton
          id="shares-button"
          sx={styles.button}
          onClick={() => scrollToSection('shares')}
        >
          <ListItemText primary={t('advocacy:shares')} />
        </ListItemButton>
        <ListItemButton
          id="advocates-button"
          sx={styles.button}
          onClick={() => scrollToSection('advocates')}
        >
          <ListItemText primary={t('advocacy:advocates')} />
        </ListItemButton>
        <ListItemButton
          id="executives-button"
          sx={styles.button}
          onClick={() => scrollToSection('executives')}
        >
          <ListItemText primary={t('advocacy:executives')} />
        </ListItemButton>
        <ListItemButton
          id="takeawaysAndNextSteps-button"
          sx={styles.button}
          onClick={() => scrollToSection('takeawaysAndNextSteps')}
        >
          <ListItemText primary={t('advocacy:takeawaysAndNextSteps')} />
        </ListItemButton>
      </List>
    </Box>
  );
};

export default AdvocacyNav;
